import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalali';
import { toLatinDigits, toPersianDigits } from './persian';

/**
 * The Jalali date adapter, showing Persian numerals.
 *
 * @remarks
 * MUI's Jalali adapter gets the calendar right and the digits wrong: it formats through
 * `date-fns-jalali`, which emits Latin numerals, so the picker would read `1405/06/19` while
 * every other number on the same screen reads `۱۴۰۵/۰۶/۱۹`. Principle P5 asks for Persian and
 * Jalali, and half of that is not the feature.
 *
 * Two methods are wrapped, in opposite directions, and both are needed:
 *
 * - `formatByString` on the way out, so the field, the calendar cells, the month header and
 *   the year list all show Persian digits. Everything the picker displays goes through it.
 * - `parse` on the way in, because a Persian keyboard produces Persian digits and the base
 *   parser reads them as nothing. Without this the field accepts what it just rendered and
 *   then reports it as an invalid date.
 *
 * The adapter's methods are class *fields* rather than prototype methods, so `super` cannot
 * reach them. They are captured after `super()` instead, which is why this is a constructor
 * rather than a pair of overrides.
 *
 * None of this touches storage. Digits are a display concern; the value handed to the
 * application is still a `Date`, and the wire format is still ISO-8601.
 */
export class PersianJalaliAdapter extends AdapterDateFnsJalali {
  constructor(...args: ConstructorParameters<typeof AdapterDateFnsJalali>) {
    super(...args);

    const baseFormatByString = this.formatByString;
    const baseParse = this.parse;

    this.formatByString = (value, formatString) =>
      toPersianDigits(baseFormatByString(value, formatString));

    this.parse = (value, formatString) => baseParse(toLatinDigits(value), formatString);
  }
}
